import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aiPodService, type AiPod } from '../services/aiPodService';
import {
  aiPodBillingService,
  formatMoney,
  planPriceForCycle,
  type BillingCycle,
  type ChangePreview,
  type PodSnapshotFeature,
} from '../services/aiPodBillingService';
import type { BillingPlanDto } from '../services/billingService';
import { Button } from '../components/ui/Button';
import PodBreadcrumb from '../components/ai-pod/PodBreadcrumb';
import { APP_CONFIG } from '../constants';

function formatDate(iso: string | null | undefined) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function featureText(f: PodSnapshotFeature): string {
  if (f.type === 'boolean') return f.value === true ? 'Included' : 'Not included';
  if (f.type === 'limit') return Number(f.value) === -1 ? 'Unlimited' : Number(f.value ?? 0).toLocaleString();
  return String(f.value ?? '—');
}

/** Features the target plan gives less of than the current one. */
function reducedFeatures(current: PodSnapshotFeature[], target: PodSnapshotFeature[]) {
  return current
    .map((cur) => {
      const next = target.find((t) => t.key === cur.key);
      if (!next) return null;
      if (cur.type === 'boolean' && cur.value === true && next.value !== true) return { name: cur.name, from: cur, to: next };
      if (cur.type === 'limit') {
        const a = Number(cur.value);
        const b = Number(next.value);
        const lower = a === -1 ? b !== -1 : b !== -1 && b < a;
        if (lower) return { name: cur.name, from: cur, to: next };
      }
      return null;
    })
    .filter((x): x is { name: string; from: PodSnapshotFeature; to: PodSnapshotFeature } => !!x);
}

const PodCheckout: React.FC = () => {
  const { podId } = useParams<{ podId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const planId = searchParams.get('planId') ?? '';
  const billingCycle: BillingCycle = searchParams.get('cycle') === 'yearly' ? 'yearly' : 'monthly';

  const [pod, setPod] = useState<AiPod | null>(null);
  const [plan, setPlan] = useState<BillingPlanDto | null>(null);
  const [preview, setPreview] = useState<ChangePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const price = useMemo(() => (plan ? planPriceForCycle(plan, billingCycle) : null), [plan, billingCycle]);

  useEffect(() => {
    if (!podId || !planId) {
      setFetchError('Choose a plan first');
      setLoading(false);
      return;
    }
    setLoading(true);
    setFetchError(null);
    (async () => {
      try {
        const p = await aiPodService.getAiPodById(podId);
        setPod(p);
        const { plans } = await aiPodBillingService.listBillingPlans(podId);
        const found = (plans || []).find((pl) => pl.id === planId) ?? null;
        setPlan(found);
        if (!found) throw new Error('This plan is no longer available');
        const chosen = planPriceForCycle(found, billingCycle);
        if (!chosen) throw new Error(`${found.displayName} is not available with ${billingCycle} billing`);
        setPreview(await aiPodBillingService.previewChange(podId, planId, chosen.interval));
      } catch (err: unknown) {
        setFetchError(err instanceof Error ? err.message : 'Failed to load checkout');
      } finally {
        setLoading(false);
      }
    })();
  }, [podId, planId, billingCycle]);

  const handleConfirm = async () => {
    if (!podId || !plan || !price) return;
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const res = await aiPodBillingService.subscribe({
        planId: plan.id,
        interval: price.interval,
        successUrl: `${origin}/pods/${podId}/manage?checkout=success`,
        cancelUrl: `${origin}/pods/${podId}/checkout?planId=${encodeURIComponent(plan.id)}&cycle=${billingCycle}`,
      });
      if (res.kind === 'checkout') {
        window.location.href = res.checkoutUrl;
        return;
      }
      if (res.kind === 'activated') toast.success('Your pod is activated.');
      if (res.kind === 'changed') {
        toast.success(
          res.effective === 'now'
            ? 'Your plan has been updated.'
            : `Your plan will change on ${formatDate(res.effectiveAt)}.`
        );
      }
      navigate(`/pods/${podId}/manage`);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-agentic-gray-50">
        <span className="loading-spinner" />
      </div>
    );
  }

  if (fetchError || !pod || !plan || !price || !preview) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-agentic-gray-50">
        <p className="text-lg font-semibold text-agentic-text-primary">{fetchError ?? 'Could not load checkout'}</p>
        <Link to={podId ? `/pods/${podId}/plans` : '/agent-types?tab=pods'} className="text-sm text-agentic-primary-blue underline">
          {podId ? 'Back to plans' : `Back to ${APP_CONFIG.PODS_TAB_LABEL}`}
        </Link>
      </div>
    );
  }

  const isChange = preview.kind !== 'new';
  const isFree = price.amountMinor === 0;
  const trialDays = preview.kind === 'new' && !isFree ? preview.trialDays : 0;
  const trialEnds = trialDays > 0 ? new Date(Date.now() + trialDays * 86_400_000).toISOString() : null;
  const lost = preview.kind === 'upgrade' || preview.kind === 'downgrade' ? reducedFeatures(preview.current.features, preview.target.features) : [];

  let title = 'Confirm your subscription';
  let confirmLabel = isFree ? 'Activate for free' : trialDays > 0 ? `Start ${trialDays}-day free trial` : 'Continue to payment';
  let note: React.ReactNode = isFree
    ? 'This plan is free. No payment details are needed.'
    : trialDays > 0
      ? `You won't be charged during the ${trialDays}-day trial. Your first payment of ${formatMoney(price.amountMinor, price.currency)} is taken on ${formatDate(trialEnds)}, when your paid ${price.interval}ly subscription starts. Cancel before then and you won't be charged.`
      : `You'll enter your card details securely with Stripe on the next step. Your subscription starts immediately.`;

  if (preview.kind === 'upgrade') {
    title = 'Confirm plan change';
    confirmLabel = preview.requiresCheckout ? 'Continue to payment' : 'Change plan now';
    note = preview.requiresCheckout
      ? 'Moving to a paid plan needs a payment method. You will be taken to Stripe to add one.'
      : preview.current.plan.interval !== preview.target.plan.interval
        ? `Your new ${preview.target.plan.interval}ly plan starts today. You're charged now for it, less a credit for the unused part of your current plan, and your renewal date moves to one ${preview.target.plan.interval} from today.`
        : "The change applies immediately. You're charged now for the price difference for the rest of the current billing period; your renewal date stays the same.";
  }
  if (preview.kind === 'downgrade') {
    title = 'Schedule plan change';
    confirmLabel = 'Schedule change';
    note = `You keep ${preview.current.plan.displayName} until ${formatDate(preview.effectiveAt)}. Your new plan and price start then. You can undo this from Manage subscription before that date.`;
  }
  if (preview.kind === 'same') {
    title = 'You are already on this plan';
  }

  return (
    <div className="min-h-screen bg-agentic-gray-50">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <PodBreadcrumb
          items={[
            { label: pod.name, to: isChange ? `/pods/${pod.id}/manage` : `/pods/${pod.id}` },
            { label: isChange ? 'Change plan' : 'Plans', to: `/pods/${pod.id}/plans` },
            { label: 'Confirm' },
          ]}
        />
        <h1 className="mb-6 text-3xl font-bold text-kv-agentic-body">{title}</h1>

        <div className="rounded-panel bg-white p-6 shadow-card space-y-5">
          {isChange && (preview.kind === 'upgrade' || preview.kind === 'downgrade') && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-agentic-gray-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-agentic-text-muted">Current</p>
                <p className="mt-1 font-semibold text-agentic-text-primary">{preview.current.plan.displayName}</p>
                <p className="text-sm text-agentic-text-secondary">
                  {formatMoney(preview.current.plan.amountMinor, preview.current.plan.currency)} / {preview.current.plan.interval}
                </p>
              </div>
              <div className="rounded-xl border-2 border-kv-agentic-blue p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-agentic-text-muted">
                  {preview.kind === 'downgrade' ? `From ${formatDate(preview.effectiveAt)}` : 'New'}
                </p>
                <p className="mt-1 font-semibold text-agentic-text-primary">{preview.target.plan.displayName}</p>
                <p className="text-sm text-agentic-text-secondary">
                  {isFree ? 'Free' : `${formatMoney(preview.target.plan.amountMinor, preview.target.plan.currency)} / ${preview.target.plan.interval}`}
                </p>
              </div>
            </div>
          )}

          {!isChange && (
            <div className="flex items-center justify-between gap-4 border-b border-agentic-gray-100 pb-4">
              <div>
                <p className="font-semibold text-agentic-text-primary">
                  {pod.name} · {plan.displayName}
                </p>
                <p className="text-sm text-agentic-text-muted">{isFree ? 'Free plan' : `Billed ${price.interval}ly`}</p>
              </div>
              <p className="text-xl font-bold text-kv-agentic-body">
                {isFree ? 'Free' : `${formatMoney(price.amountMinor, price.currency)} / ${price.interval}`}
              </p>
            </div>
          )}

          {lost.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-800">With the new plan you'll have less of:</p>
              <ul className="mt-2 space-y-1 text-sm text-amber-800">
                {lost.map((l) => (
                  <li key={l.name}>
                    {l.name}: {featureText(l.from)} → {featureText(l.to)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-sm leading-relaxed text-agentic-text-secondary">{note}</p>

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <Button variant="ghost" size="md" onClick={() => navigate(`/pods/${pod.id}/plans`)}>
              Choose another plan
            </Button>
            {preview.kind !== 'same' && (
              <Button variant="primary" size="md" loading={submitting} onClick={handleConfirm}>
                {submitting ? 'Working…' : confirmLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PodCheckout;
